import { createPortal } from 'react-dom';
import { convertIsoUTCToLocal } from '../../libs.jsx';
import useInfoPopover from './useInfoPopover.js';
import './notes-cell.css';

/**
 * NotesCell — inline notes indicator for event/alert rows.
 *
 * Replaces the legacy "Add Note" kebab item with a visible, scannable badge:
 *   - notes.length === 0 → muted ghost button (still clickable to add)
 *   - notes.length  >  0 → filled badge with count; hovering or focusing it shows a
 *                          preview popover of the 3 most recent notes
 * Click always fires `onClick(row)` so the host page can open its existing
 * EventNoteDialog (which supports both viewing existing notes and adding new).
 *
 * @param {{
 *   row:               object,            // event row passed back to onClick
 *   notes?:            Array<{ note_id, note, create_time, display_name }>,
 *   onClick:           (row) => void,
 *   eventNotesActive?: boolean,           // when false, renders an inert "—"
 *   tzOffset?:         number,            // for popover timestamps
 *   ariaPrefix?:       string,            // optional context for aria-label
 * }} props
 */
const MAX_PREVIEW = 3;
const PREVIEW_W = 320;

const NotesCell = ({ row, notes, onClick, eventNotesActive = true, tzOffset, ariaPrefix }) => {
  const list = Array.isArray(notes) ? notes : [];
  const count = list.length;
  /* Describe mode: the badge is a real button (it opens the notes dialog), so the
     preview adds no tab stop or pin of its own; a tap goes straight to the dialog,
     which shows every note. */
  const { open, style, panelId, triggerProps, close } = useInfoPopover({
    maxWidth: PREVIEW_W, flipBelow: 200, enabled: eventNotesActive && count > 0, mode: 'describe',
  });

  // Feature gate: render an inert placeholder rather than a clickable affordance
  if (!eventNotesActive) {
    return <span className="nc-disabled" aria-hidden="true">{'—'}</span>;
  }

  const handleClick = (e) => {
    e.stopPropagation();
    close();
    onClick?.(row);
  };

  // Empty state — ghost "+" affordance
  if (count === 0) {
    const label = `${ariaPrefix ? ariaPrefix + ' — ' : ''}Add note`;
    return (
      <span className="nc-cell">
        <button
          type="button"
          className="nc-add"
          onClick={handleClick}
          aria-label={label}
          title="Add note"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            <line x1="12" y1="8" x2="12" y2="14" />
            <line x1="9" y1="11" x2="15" y2="11" />
          </svg>
        </button>
      </span>
    );
  }

  // Notes exist — count badge with hover popover preview
  const label = `${ariaPrefix ? ariaPrefix + ' — ' : ''}View ${count} note${count !== 1 ? 's' : ''}`;
  const recent = list.slice(-MAX_PREVIEW).reverse();
  const extra = count - recent.length;

  return (
    <span className="nc-cell" {...triggerProps}>
      <button
        type="button"
        className="nc-badge"
        onClick={handleClick}
        aria-label={label}
        aria-describedby={open ? panelId : undefined}
        title={`${count} note${count !== 1 ? 's' : ''}`}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        {count}
      </button>
      {open && style ? createPortal(
        <span id={panelId} className="nc-popover" style={style} role="tooltip">
          <span className="nc-popover__title">Notes ({count})</span>
          <ul className="nc-popover__list">
            {recent.map((n, i) => (
              <li key={n.note_id || i} className="nc-popover__item">
                <span className="nc-popover__meta">
                  <span className="nc-popover__author">{n.display_name || 'Unknown'}</span>
                  <span>{n.create_time ? convertIsoUTCToLocal(n.create_time, tzOffset) : ''}</span>
                </span>
                <span className="nc-popover__text">{n.note || ''}</span>
              </li>
            ))}
          </ul>
          {extra > 0 ? (
            <span className="nc-popover__more">+ {extra} earlier note{extra !== 1 ? 's' : ''}</span>
          ) : null}
          <p className="nc-popover__hint">Click to view all or add a note</p>
        </span>,
        document.body,
      ) : null}
    </span>
  );
};

export default NotesCell;
